import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { START_FREE } from '../../../content/ctaLabels';

// The small pieces every section of the landing page shares. One sign-up
// button, one label ("Start free", founder pick A 2026-09-29), one source
// line style, so a section cannot drift into its own version of any of them.

/**
 * The page's sign-up button. `cta` is its id in content/landingIds.js
 * (data-cta), named for what the button means, never its text, and it is
 * also the sign-in entry the button reports: onStart receives it.
 */
export function StartFree({ onStart, cta, tone = 'green', size = 'lg', className = '' }) {
    const tones = {
        green: 'bg-[#1A4D2E] text-white hover:bg-[#2D6B45]',
        white: 'bg-white text-[#1A4D2E] hover:bg-[#F0F7F1]',
    };
    const sizes = {
        lg: 'h-14 px-8 text-lg',
        md: 'h-12 px-7 text-base',
    };
    return (
        <button
            type="button"
            onClick={() => onStart(cta)}
            data-cta={cta}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold transition-colors ${tones[tone]} ${sizes[size]} ${className}`}
        >
            {START_FREE}
            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
    );
}

/** The secondary button: same height and radius as StartFree, a 1px border. */
export const SECONDARY_BUTTON = 'inline-flex h-14 items-center justify-center gap-2 rounded-xl border border-[#1A4D2E]/30 bg-white px-8 text-lg font-semibold text-[#1A4D2E] transition-colors hover:border-[#1A4D2E]/60';

/** The one eyebrow: small gold capitals, no pill (on green: the light tint). */
export function Eyebrow({ children, dark = false }) {
    return (
        <p className={`mb-3 text-xs font-bold uppercase tracking-wider ${dark ? 'text-[#E3F0E2]' : 'text-[#8A6B1F]'}`}>
            {children}
        </p>
    );
}

/** The one badge: a green-tint pill. */
export function Badge({ children }) {
    return (
        <span className="inline-flex items-center rounded-full border border-[#A8C5A6] bg-[#F0F7F1] px-2.5 py-0.5 text-xs font-medium text-[#1A4D2E]">
            {children}
        </span>
    );
}

/** The one link arrow (the same icon the buttons carry). */
export function Arrow() {
    return <ArrowRightIcon className="h-4 w-4 flex-none" aria-hidden="true" />;
}

/** A figure's source, small, right under it (app-page rule 5: on the row itself). */
export function Source({ children, dark = false, className = '' }) {
    return (
        <span className={`mt-1 block text-xs leading-snug [text-wrap:balance] ${dark ? 'text-[#E3F0E2]' : 'text-[#6B6B6B]'} ${className}`}>
            {children}
        </span>
    );
}
